import type { ApiResponse, Checkout, PixPayment, ShippingRate } from "@melhot/shared";

export const API_BASE_URL =
  (typeof window !== "undefined" && (window as any).__MELHOT_API_BASE_URL__) ||
  import.meta.env.VITE_API_BASE_URL ||
  "https://onhpfmicbocuzirbwgrs.supabase.co/functions/v1/api";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) }
  });
  const payload = (await response.json()) as ApiResponse<T>;
  if ("error" in payload) throw new Error(payload.error.message);
  return payload.data;
}

export const api = {
  createSession: () => request<Checkout>("/api/checkout/sessions", { method: "POST", body: "{}" }),
  createSessionFromShopifyCart: (body: unknown) =>
    request<Checkout>("/api/checkout/sessions/from-shopify-cart", { method: "POST", body: JSON.stringify(body) }),
  getSession: (id: string) => request<Checkout>(`/api/checkout/sessions/${id}`),
  updateSession: (id: string, body: unknown) => request<Checkout>(`/api/checkout/sessions/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  applyCoupon: (id: string, code: string) => request<Checkout>(`/api/checkout/sessions/${id}/coupon`, { method: "POST", body: JSON.stringify({ code }) }),
  removeCoupon: (id: string) => request<Checkout>(`/api/checkout/sessions/${id}/coupon`, { method: "DELETE" }),
  lookupAddress: async (postalCode: string) => {
    const cleanCep = postalCode.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await res.json();
        if (!data.erro) {
          return {
            postalCode: cleanCep,
            street: data.logradouro || "",
            neighborhood: data.bairro || "",
            city: data.localidade || "",
            state: data.uf || "",
            country: "BR" as const
          };
        }
      } catch {
        // Fallback
      }
    }
    return request<any>("/api/address/lookup", { method: "POST", body: JSON.stringify({ postalCode }) });
  },
  shippingRates: (body: unknown) => request<ShippingRate[]>("/api/shipping/rates", { method: "POST", body: JSON.stringify(body) }),
  createPix: (checkoutSessionId: string) => request<PixPayment>("/api/payments/pix", { method: "POST", body: JSON.stringify({ checkoutSessionId }) }),
  getPayment: (id: string) => request<PixPayment>(`/api/payments/${id}`),
  paymentStatus: (id: string) => request<{ id: string; status: PixPayment["status"]; expiresAt: string; paidAt?: string; terminal: boolean }>(`/api/checkout/payments/${id}/status`).catch(() => request<{ id: string; status: PixPayment["status"]; expiresAt: string; paidAt?: string; terminal: boolean }>(`/api/payments/${id}/status`)),
  approveDevPayment: (id: string) => request(`/dev/payments/${id}/approve`, { method: "POST" }),
  getConfig: () => request<any>("/api/checkout/config")
};
